import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  getCurrentUser,
  getNutritionistProfilePreferences,
  saveNutritionistProfilePreferences,
  updateStoredUserProfile,
  updateUserProfile,
  type NutritionistProfilePreferences,
  type NutritionistProfilePreferencesPayload,
} from '@/lib/apiService';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { getCurrencyForCountry } from '@/utils/countryCurrency';
import { countryNameToCode } from '@/utils/country-code-mapping';
import countryOptionsData from '@/data/countries.json';
import menuBarLogo from '@/assets/images/menu-bar-logo.svg';
import { PROFESSIONAL_QUALIFICATION_OPTIONS } from '@/constants/professional-qualifications';

const authBackground = '/images/auth/background.svg';

const practiceSetupSchema = z.object({
  practiceName: z.string().trim().optional(),
  qualification: z.string().trim().optional(),
  country: z.string().length(2, 'Country must be a 2-letter code'),
  zipCode: z.string().trim().min(3, 'Zip code is required'),
  preferredMeasurementSystem: z.string().optional(),
  currency: z.string().trim().optional(),
});

type PracticeSetupFormValues = z.infer<typeof practiceSetupSchema>;

type CountryOption = {
  value: string;
  label: string;
};

const COUNTRY_OPTIONS: CountryOption[] = (countryOptionsData as CountryOption[])
  .map((country) => ({
    label: country.label,
    value: countryNameToCode[country.value] || '',
  }))
  .filter((country) => /^[A-Z]{2}$/.test(country.value))
  .sort((left, right) => left.label.localeCompare(right.label));

const getCurrencyDisplayName = (currencyCode: string): string => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'currency' }).of(currencyCode) || currencyCode;
  } catch {
    return currencyCode;
  }
};

type PracticeSetupPageProps = {
  accountType: 'individual' | 'clinic';
};

const FIELD_CLASSNAME =
  '!h-11 !rounded-md !border-[#d7dde5] !bg-white text-sm text-foreground [&_input::placeholder]:text-muted-foreground';
const FIELD_LABEL_CLASSNAME = 'text-xs font-semibold text-slate-600';
const SELECT_FIELD_CLASSNAME = `${FIELD_CLASSNAME} !px-3 text-left [&>span]:line-clamp-1 [&>span]:block [&>span]:w-full [&>span]:text-left [&_[data-placeholder]]:!text-muted-foreground`;

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

const readNullableString = (value: unknown): string | null => (
  typeof value === 'string' ? value : null
);

const readNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const splitName = (fullName: string): { firstName: string; lastName: string } => {
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' '),
  };
};

const buildProfilePreferencesPayload = (
  data: PracticeSetupFormValues,
  existingPreferences: NutritionistProfilePreferences | null,
): NutritionistProfilePreferencesPayload => {
  const currentUser = getCurrentUser();
  const storedFullName = readString(currentUser?.fullName);
  const { firstName, lastName } = splitName(storedFullName);
  const existingPersonalDetails = existingPreferences?.personalDetails || {};
  const existingContactInfo = existingPreferences?.contactInfo || {};
  const existingProfessionalBio =
    typeof existingPreferences?.professionalBio === 'string'
      ? { bio: existingPreferences.professionalBio }
      : existingPreferences?.professionalBio || {};
  const existingCredentials = existingPreferences?.credentials || {};

  return {
    personalDetails: {
      firstName: readString(existingPersonalDetails.firstName, firstName),
      lastName: readString(existingPersonalDetails.lastName, lastName),
      displayName: readString(existingPersonalDetails.displayName, storedFullName),
      pronouns: readString(existingPersonalDetails.pronouns),
    },
    contactInfo: {
      phone: readString(existingContactInfo.phone, currentUser?.phone),
      phoneCountryCode: readString(existingContactInfo.phoneCountryCode, currentUser?.phoneCountryCode),
      country: data.country,
      zipCode: data.zipCode.trim(),
      linkedInAccount: existingContactInfo.linkedInAccount ?? null,
      instagramAccount: existingContactInfo.instagramAccount ?? null,
    },
    professionalBio: {
      bio: readString(existingProfessionalBio.bio),
    },
    credentials: {
      primaryQualification: data.qualification || readString(existingCredentials.primaryQualification),
      registrationBody: readNullableString(existingCredentials.registrationBody),
      registrationNumber: readString(existingCredentials.registrationNumber),
      registrationExpiry: readString(existingCredentials.registrationExpiry),
      experienceYears: readNullableNumber(existingCredentials.experienceYears),
      specializations: Array.isArray(existingCredentials.specializations) ? existingCredentials.specializations : [],
    },
  };
};

const PracticeSetupPage = ({ accountType }: PracticeSetupPageProps) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const welcomeText = useMemo(() => {
    const user = getCurrentUser();
    const firstName = user?.fullName?.trim().split(/\s+/)[0];
    if (!firstName) {
      return 'WELCOME';
    }
    return `WELCOME ${firstName.toUpperCase()}`;
  }, []);

  const form = useForm<PracticeSetupFormValues>({
    resolver: zodResolver(practiceSetupSchema),
    defaultValues: {
      practiceName: '',
      qualification: '',
      country: '',
      zipCode: '',
      preferredMeasurementSystem: '',
      currency: '',
    },
  });
  const selectedCountry = form.watch('country');
  const selectedCurrency = form.watch('currency') || getCurrencyForCountry(selectedCountry) || '';

  const onSubmit = async (data: PracticeSetupFormValues) => {
    setIsSaving(true);
    try {
      const inferredCurrency = data.currency?.trim() || getCurrencyForCountry(data.country) || '';
      const profilePayload: Record<string, unknown> = {
        accountType,
        country: data.country,
        onboardingCompletedAt: new Date().toISOString(),
      };

      if (data.qualification?.trim()) {
        profilePayload.qualification = data.qualification.trim();
      }

      if (data.practiceName) {
        profilePayload.practiceName = data.practiceName.trim();
      }

      if (data.preferredMeasurementSystem) {
        profilePayload.preferredMeasurementSystem = data.preferredMeasurementSystem;
      }

      if (inferredCurrency) {
        profilePayload.currency = inferredCurrency;
      }

      const existingProfilePreferences = await getNutritionistProfilePreferences().catch(() => null);
      const profilePreferencesPayload = buildProfilePreferencesPayload(data, existingProfilePreferences);

      await updateUserProfile(profilePayload);
      await saveNutritionistProfilePreferences(profilePreferencesPayload);
      updateStoredUserProfile({
        country: data.country,
        ...(inferredCurrency ? { currency: inferredCurrency } : {}),
      });

      showSuccessToast('Practice setup saved', 'Welcome to your dashboard.');
      navigate('/dashboard');
    } catch (error) {
      showErrorToast(error, 'Could not save practice setup');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-dvh bg-white p-4 sm:p-6 lg:p-8">
      <section
        className="relative mx-auto min-h-[calc(100dvh-2rem)] w-full overflow-x-hidden rounded-3xl border border-slate-200 px-4 py-4 shadow-[0_24px_80px_rgba(2,35,48,0.14)] sm:min-h-[calc(100dvh-3rem)] sm:px-6 sm:py-6 lg:min-h-[calc(100dvh-4rem)]"
        style={{
          backgroundImage: `url(${authBackground})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="pointer-events-none absolute left-1/2 top-[16%] h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-[#9fe1f7]/40 blur-3xl" />

        <div className="relative z-10 flex min-h-full items-start justify-center pt-2 sm:pt-3">
          <div className="w-full max-w-[600px]">
            <div
              className="w-full min-h-[620px] rounded-2xl border border-white bg-white px-4 py-5 shadow-none sm:min-h-[700px] sm:px-6 sm:py-6 lg:min-h-[760px]"
              style={{ backgroundColor: '#ffffff' }}
            >
            <div className="mx-auto max-w-[520px]">
              <img src={menuBarLogo} alt="Calorie Science" className="mx-auto h-10 w-auto max-w-[230px]" />

              <p className="mt-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-700">
                {welcomeText}
              </p>
              <h1 className="mt-3 text-center text-3xl font-semibold leading-tight text-slate-800">Let&apos;s set up your practice</h1>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="mt-9 space-y-5">
                  <div className="pb-5 sm:pb-7">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="practiceName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>Practice Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your practice name" className={FIELD_CLASSNAME} {...field} />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="qualification"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>Professional Qualification</FormLabel>
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className={SELECT_FIELD_CLASSNAME}>
                                  <SelectValue placeholder="Select professional qualification" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {PROFESSIONAL_QUALIFICATION_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>Country*</FormLabel>
                            <Select
                              value={field.value || undefined}
                              onValueChange={(value) => {
                                  field.onChange(value);
                                  form.setValue('currency', getCurrencyForCountry(value) ?? '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }}
                            >
                              <FormControl>
                                <SelectTrigger className={SELECT_FIELD_CLASSNAME}>
                                  <SelectValue placeholder="Select your country" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {COUNTRY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>Zip code*</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your zip code" className={FIELD_CLASSNAME} {...field} />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <p className="mb-3 text-sm font-semibold text-slate-700">Preferences</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="preferredMeasurementSystem"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>System of Measurement</FormLabel>
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className={SELECT_FIELD_CLASSNAME}>
                                  <SelectValue placeholder="Select measurement system" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="metric">Kg/Cm</SelectItem>
                                <SelectItem value="imperial">Lb/In</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="currency"
                        render={() => (
                          <FormItem>
                            <FormLabel className={FIELD_LABEL_CLASSNAME}>Billing currency*</FormLabel>
                            <FormControl>
                              <Input
                                value={selectedCurrency ? getCurrencyDisplayName(selectedCurrency) : ''}
                                placeholder="Auto-selected from country"
                                readOnly
                                tabIndex={-1}
                                aria-readonly="true"
                                className={`${FIELD_CLASSNAME} !bg-slate-50 !text-slate-900 [&_input]:!text-slate-900 [&_input]:!opacity-100`}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="pt-14 text-center sm:pt-16">
                    <Button
                      type="submit"
                      disabled={isSaving}
                      className="!h-11 !w-full max-w-[190px] !rounded-md !bg-[#48AEE2] !text-sm !font-semibold !text-white hover:!bg-[#3098cd] disabled:!bg-[#a9d5e7]"
                    >
                      {isSaving ? 'Saving...' : 'Continue'}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PracticeSetupPage;
